import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlertTriangle, Star } from 'lucide-react'
import { useReviews } from '../../hooks/useReputation'
import { playAlertBeep } from '../../lib/sound'
import type { Review } from '../../types/reputation'

const STORAGE_KEY = 'review-alerts-seen'

function loadSeen(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function saveSeen(seen: Set<string>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seen)))
  } catch {
    // ignore quota errors
  }
}

function isNegative(review: Review) {
  return review.rating <= 3 && !review.responded
}

export function ReviewAlert() {
  const navigate = useNavigate()
  const { data: reviews } = useReviews()
  const seenRef = useRef<Set<string>>(loadSeen())

  useEffect(() => {
    if (!reviews) return
    const seen = seenRef.current
    const newNegatives = reviews.filter((r) => isNegative(r) && !seen.has(r.id))
    if (newNegatives.length === 0) return

    playAlertBeep()

    for (const review of newNegatives) {
      seen.add(review.id)
      toast.custom(
        (t) => (
          <div
            className={`max-w-md w-full bg-surface-1 border-2 border-error-500 rounded-xl shadow-xl p-4 flex items-start gap-3 ${
              t.visible ? 'animate-enter' : 'animate-leave'
            }`}
          >
            <div className="p-2 rounded-lg bg-error-50 text-error-600 flex-shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">Review negativa nueva</p>
              <div className="flex items-center gap-1 mt-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i < review.rating ? 'text-warning-500 fill-warning-500' : 'text-gray-300'}`}
                  />
                ))}
                <span className="text-xs text-text-tertiary ml-1">{review.platform}</span>
              </div>
              {review.content && (
                <p className="text-xs text-text-secondary mt-1.5 line-clamp-2">{review.content}</p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    toast.dismiss(t.id)
                    navigate('/reputation')
                  }}
                  className="text-xs font-medium bg-error-600 text-white px-3 py-1.5 rounded-lg hover:bg-error-700"
                >
                  Responder
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs font-medium text-text-secondary px-3 py-1.5 rounded-lg hover:bg-surface-2"
                >
                  Descartar
                </button>
              </div>
            </div>
          </div>
        ),
        {
          id: `review-${review.id}`,
          duration: Infinity,
          position: 'top-right',
        },
      )
    }

    saveSeen(seen)
  }, [reviews, navigate])

  return null
}
